import React from 'react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING } from '../../styles/emziCI';
import StructuredListInput from './StructuredListInput';
import { BILINGUAL_REQUIRED } from '../../utils/formValidation';

/**
 * EnhancedDirectionForm - Direction pack form with Input Quality Layer enhancements
 * Focus: Strategic goals, key messages, internal strategy (SINGLE LANGUAGE)
 * 
 * Enhanced with Campaign Direction fields:
 * - Why Trust (from research)
 * - Why Buy (purchase triggers)
 * - Customer Pain Insights
 */
const EnhancedDirectionForm = ({ 
  content = {}, 
  onChange, 
  errors = {}, 
  touched = {},
  onFieldBlur,
  packType = 'direction'
}) => {
  const handleChange = (field, value) => {
    onChange({ ...content, [field]: value });
  };

  const requiresBilingual = BILINGUAL_REQUIRED[packType];

  return (
    <div style={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: EMZI_SPACING.md,
    }}>
      {/* Single Language Notice */}
      <div style={{
        padding: EMZI_SPACING.sm,
        background: '#EFF6FF',
        borderRadius: EMZI_RADIUS.sm,
        fontSize: '12px',
        color: '#1E40AF',
        borderLeft: `3px solid ${EMZI_COLORS.info}`,
      }}>
        ℹ️ <strong>Internal Strategy Pack:</strong> Direction packs are for internal strategy only. Single-language entry is sufficient (BM or EN).
      </div>

      {/* WHY TRUST - Structured List */}
      <StructuredListInput
        label="Why Trust (From Research)"
        items={content.why_trust || []}
        onChange={(items) => handleChange('why_trust', items)}
        minItems={1}
        maxItems={10}
        placeholder="Add a trust reason..."
        helper="What makes customers trust this product/service? Example: 'Ustaz Kazim's trusted recipe' or 'Clinically proven ingredients'. Include research source if available."
        required={true}
        hasError={touched.why_trust && errors.content?.why_trust}
        errorMessage={errors.content?.why_trust}
      />

      {/* WHY BUY - Structured List */}
      <StructuredListInput
        label="Why Buy (Purchase Triggers)"
        items={content.why_buy || []}
        onChange={(items) => handleChange('why_buy', items)}
        minItems={1}
        maxItems={10}
        placeholder="Add a purchase trigger..."
        helper="What triggers the purchase decision? Example: '30-day money-back guarantee' or 'Limited time discount'. Based on conversion research."
        required={true}
        hasError={touched.why_buy && errors.content?.why_buy}
        errorMessage={errors.content?.why_buy}
      />

      {/* Customer Pain Insights - Structured List */}
      <StructuredListInput
        label="Customer Pain Insights"
        items={content.customer_pain_insights || []}
        onChange={(items) => handleChange('customer_pain_insights', items)}
        minItems={0}
        maxItems={10}
        placeholder="Add a pain point..."
        helper="What problems/pains do customers face? Example: 'High blood sugar' or 'Difficulty sleeping'. From customer research."
        required={false}
        hasError={false}
      />

      {/* Strategic Goals - Structured List */}
      <StructuredListInput
        label="Strategic Goals"
        items={content.strategic_goals || []}
        onChange={(items) => handleChange('strategic_goals', items)}
        minItems={1}
        maxItems={10}
        placeholder="Add a strategic goal..."
        helper="High-level objectives for this initiative. Example: 'Increase market share in Klang Valley by 15%' or 'Position as premium health brand'"
        required={true}
        hasError={touched.strategic_goals && errors.content?.strategic_goals}
        errorMessage={errors.content?.strategic_goals}
      />

      {/* Key Messages - Structured List */}
      <StructuredListInput
        label="Key Messages"
        items={content.key_messages || []}
        onChange={(items) => handleChange('key_messages', items)}
        minItems={1}
        maxItems={12}
        placeholder="Add a key message..."
        helper="Core communication points. Example: 'We are the trusted choice for Muslim families' or 'Quality without compromise'"
        required={true}
        hasError={touched.key_messages && errors.content?.key_messages}
        errorMessage={errors.content?.key_messages}
      />

      {/* Target Segments */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Target Segments
          <span style={{ color: EMZI_COLORS.error, marginLeft: '4px' }}>*</span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 Who we're targeting. Example: "Urban professionals aged 25-40, health-conscious Muslims, middle to upper income"
        </div>

        <textarea
          value={content.target_segments || ''}
          onChange={(e) => handleChange('target_segments', e.target.value)}
          onBlur={() => onFieldBlur && onFieldBlur('target_segments')}
          placeholder="e.g., Urban professionals aged 25-40, health-conscious Muslims, middle to upper income"
          rows={3}
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${touched.target_segments && errors.content?.target_segments ? EMZI_COLORS.error : EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontFamily: 'inherit',
            outline: 'none',
            resize: 'vertical',
          }}
        />
        {touched.target_segments && errors.content?.target_segments && (
          <div style={{
            marginTop: '4px',
            fontSize: '12px',
            color: EMZI_COLORS.error,
          }}>
            {errors.content.target_segments}
          </div>
        )}
      </div>

      {/* Competitive Positioning - Optional */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Competitive Positioning
          <span style={{
            fontSize: '11px',
            color: EMZI_COLORS.textMuted,
            marginLeft: EMZI_SPACING.xs,
            fontWeight: '400',
          }}>
            (Optional)
          </span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 How we differentiate from competitors. Example: "Unlike generic supplements, we focus exclusively on halal-certified, locally-sourced ingredients"
        </div>

        <textarea
          value={content.competitive_positioning || ''}
          onChange={(e) => handleChange('competitive_positioning', e.target.value)}
          placeholder="e.g., Unlike generic supplements, we focus exclusively on halal-certified, locally-sourced ingredients"
          rows={3}
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontFamily: 'inherit',
            outline: 'none',
            resize: 'vertical',
          }}
        />
      </div>

      {/* Success Metrics - Optional */}
      <StructuredListInput
        label="Success Metrics"
        items={content.success_metrics || []}
        onChange={(items) => handleChange('success_metrics', items)}
        minItems={0}
        maxItems={8}
        placeholder="Add a success metric..."
        helper="How we measure success. Example: '20% increase in conversion rate' or 'Net Promoter Score above 50'"
        required={false}
        hasError={false}
      />

      {/* Campaign Timeframe - Optional */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: EMZI_SPACING.md,
      }}>
        {/* Start Date */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: EMZI_SPACING.xs,
            color: EMZI_COLORS.textPrimary,
          }}>
            Strategy Start Date
            <span style={{
              fontSize: '11px',
              color: EMZI_COLORS.textMuted,
              marginLeft: EMZI_SPACING.xs,
              fontWeight: '400',
            }}>
              (Optional)
            </span>
          </label>
          
          <input
            type="date"
            value={content.start_date || ''}
            onChange={(e) => handleChange('start_date', e.target.value)}
            style={{
              width: '100%',
              padding: EMZI_SPACING.sm,
              border: `1px solid ${EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.md,
              fontSize: '14px',
              outline: 'none',
            }}
          />
        </div>

        {/* End Date */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: EMZI_SPACING.xs,
            color: EMZI_COLORS.textPrimary,
          }}>
            Strategy End Date
            <span style={{
              fontSize: '11px',
              color: EMZI_COLORS.textMuted,
              marginLeft: EMZI_SPACING.xs,
              fontWeight: '400',
            }}>
              (Optional)
            </span>
          </label>
          
          <input
            type="date"
            value={content.end_date || ''}
            onChange={(e) => handleChange('end_date', e.target.value)}
            style={{
              width: '100%',
              padding: EMZI_SPACING.sm,
              border: `1px solid ${EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.md,
              fontSize: '14px',
              outline: 'none',
            }}
          />
        </div>
      </div>

      {/* Completion hint */}
      <div style={{
        padding: EMZI_SPACING.sm,
        background: '#e6f7ed',
        borderRadius: EMZI_RADIUS.sm,
        fontSize: '12px',
        color: EMZI_COLORS.success,
        borderLeft: `3px solid ${EMZI_COLORS.success}`,
      }}>
        ✅ <strong>Pro Tip:</strong> Direction packs guide internal strategy. Be clear on goals and how agents should position the brand!
      </div>
    </div>
  );
};

export default EnhancedDirectionForm;
